import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { UserContext } from "../../../App";
import Table from "../classlistspage/ClassListTable";
import Pagination from "../Pagination";
import useSortableData from "../../../hooks/useSortableData";
import useSelectableItems from "../../../hooks/useSelectableItems";
import "../../../styles/CreateButton.css";
import "../../../styles/common.css";
import MoreOptionsPane from "../../MoreOptionsPane";
import "../../../styles/MoreOptionsButton.css";
import CoursePopUp from "./CoursePopUp";
import CoursePopUpDuplicate from "./CoursePopUpDuplicate";
import { useTrainingButton } from "../../../hooks/useTrainingElement";

import { getPerformance, trace } from "firebase/performance";

const formatDate = (dateField) => {
  if (!dateField) return "N/A";
  const date = new Date(dateField);
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const CoursesPage = () => {
  const { uid, role } = useContext(UserContext);
  const [courses, setCourses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 15;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState("");

  // Training system
  const createButton = useTrainingButton("courses", "courses_create");

  const {
    items: sortedCourses,
    requestSort,
    sortConfig,
  } = useSortableData(courses);
  const {
    selectedItems: selectedCourses,
    handleSelectItem,
    handleBulkSelect,
  } = useSelectableItems();

  const fetchCourses = useCallback(async () => {
    const fetchCoursesTrace = trace(getPerformance(), "fetchCourses");
    fetchCoursesTrace.start();
    setIsLoading(true);
    console.log("Fetching courses...");
    try {
      const getCoursesFunction = httpsCallable(functions, "getCourses");
      const result = await getCoursesFunction();
      const fetchedCourses = result.data.courses;
      console.log("Courses fetched:", fetchedCourses.length);
      setCourses(fetchedCourses);
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setIsLoading(false);
      fetchCoursesTrace.stop();
    }
  }, []);

  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useEffect(() => {
    if (!initialFetchDone && uid && role) {
      fetchCourses();
      setInitialFetchDone(true);
    }
  }, [uid, role, initialFetchDone, fetchCourses]);

  const handleDeleteCourses = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete the selected courses?"
    );
    if (confirmDelete) {
      try {
        const deleteCourseFunction = httpsCallable(functions, "deleteCourse");
        const deletePromises = selectedCourses.map((course) =>
          deleteCourseFunction({ courseId: course.id })
        );
        await Promise.all(deletePromises);
        fetchCourses();
      } catch (error) {
        console.error("Error deleting courses: ", error);
        alert("Failed to delete selected courses.");
      }
    }
  };

  const [showCoursePopup, setShowCoursePopup] = useState(false);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [selectedCourseForDuplicate, setSelectedCourseForDuplicate] =
    useState(null);

  const handleCreateCourse = async (courseData) => {
    try {
      setIsLoading(true);
      const createCourseFunction = httpsCallable(functions, "createCourse");
      const response = await createCourseFunction({
        courseName: courseData.courseName,
        courseDescription: courseData.courseDescription || "",
      });

      console.log("Course created:", response.data);
      // Handle successful course creation (e.g., navigate to the new course page)
      if (response.data && response.data.courseId) {
        navigate(`/courses/${response.data.courseId}`);
      } else {
        alert("Course created successfully, but no course ID was returned.");
        setShowCoursePopup(false); // Only close if there's no navigation
      }
    } catch (error) {
      console.error("Error creating course: ", error);
      alert("Failed to create the course: " + error.message);
      setShowCoursePopup(false); // Close on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateCourse = async (courseData) => {
    try {
      setIsLoading(true);
      const duplicateCourseFunction = httpsCallable(
        functions,
        "duplicateCourse"
      );
      const response = await duplicateCourseFunction({
        courseId: selectedCourseForDuplicate.id,
        selectedSectionIds: courseData.selectedSectionIds,
        newCourseName: courseData.courseName,
      });

      console.log("Course duplicated:", response.data);

      if (response.data && response.data.courseId) {
        // Refresh the course list
        await fetchCourses();
        // Navigate to the new course
        navigate(`/courses/${response.data.courseId}`);
      } else {
        alert("Course duplicated successfully, but no course ID was returned.");
        setShowDuplicatePopup(false);
      }
    } catch (error) {
      console.error("Error duplicating course: ", error);
      alert("Failed to duplicate the course: " + error.message);
      setShowDuplicatePopup(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateConfirm = () => {
    if (selectedCourses.length !== 1) {
      alert("Please select exactly one course to duplicate.");
      return;
    }

    const courseToDuplicate = selectedCourses[0];
    setSelectedCourseForDuplicate(courseToDuplicate);
    setShowDuplicatePopup(true);
    setShowMoreOptionsPane(false);
  };

  const filteredCourses = useMemo(
    () =>
      sortedCourses.filter((course) =>
        course.courseName.toLowerCase().includes(filterText.toLowerCase())
      ),
    [sortedCourses, filterText]
  );

  const formattedCourses = useMemo(
    () =>
      filteredCourses.map((course) => ({
        ...course,
        creationDate: course.creationDate
          ? formatDate(course.creationDate)
          : "N/A",
        lastEditDate: course.lastEditDate
          ? formatDate(course.lastEditDate)
          : "N/A",
        ownerName: course.ownerName || "Unknown",
      })),
    [filteredCourses]
  );

  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = formattedCourses.slice(
    indexOfFirstCourse,
    indexOfLastCourse
  );

  const [showMoreOptionsPane, setShowMoreOptionsPane] = useState(false);

  const handleMoreOptionsButtonClick = (event) => {
    const rect = event.target.getBoundingClientRect();
    setShowMoreOptionsPane(true);
  };

  const handleRenameConfirm = async () => {
    if (selectedCourses.length !== 1) {
      alert("Please select exactly one course to rename.");
      return;
    }

    const courseToRename = selectedCourses[0];
    const newCourseName = prompt(
      "Enter new name for the course:",
      courseToRename.courseName
    );

    if (newCourseName && newCourseName !== courseToRename.courseName) {
      try {
        const updateCourseFunction = httpsCallable(functions, "updateCourse");
        await updateCourseFunction({
          courseId: courseToRename.id,
          courseName: newCourseName,
        });

        // Update local state
        setCourses((prevCourses) =>
          prevCourses.map((course) =>
            course.id === courseToRename.id
              ? { ...course, courseName: newCourseName }
              : course
          )
        );

        setShowMoreOptionsPane(false);
      } catch (error) {
        console.error("Error renaming course: ", error);
        alert("Failed to rename course.");
      }
    }
  };

  const handleDeleteConfirm = () => {
    handleDeleteCourses();
    setShowMoreOptionsPane(false);
  };

  return (
    <div className="courses-page">
      <div className="main-content">
        <div className="top-controls">
          <button
            ref={createButton.ref}
            className={`create-course-button ${createButton.className}`}
            onClick={() => setShowCoursePopup(true)}
            onMouseEnter={createButton.onMouseEnter}
          >
            <span className="button-text-desktop">Create a New Course</span>
            <span className="button-text-mobile">Create</span>
          </button>
          <div className="filter-container">
            <input
              type="text"
              placeholder="Filter courses..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
          </div>
        </div>
        <Table
          data={currentCourses}
          columns={[
            { key: "courseName", label: "Course Name" },
            { key: "ownerName", label: "Owner" },
            { key: "creationDate", label: "Created" },
            { key: "lastEditDate", label: "Last Edited" },
          ]}
          selectedItems={selectedCourses}
          handleSelectItem={handleSelectItem}
          handleBulkSelect={() => handleBulkSelect(formattedCourses)}
          requestSort={requestSort}
          getSortArrow={(key) =>
            sortConfig.key === key ? (
              sortConfig.direction === "ascending" ? (
                <span className="arrow up"></span>
              ) : (
                <span className="arrow down"></span>
              )
            ) : null
          }
          onRowClick={(e, course) => {
            if (e.target.tagName !== "INPUT") {
              navigate(`/courses/${course.id}`);
            }
          }}
          isLoading={isLoading}
          formatters={{
            creationDate: formatDate,
            lastEditDate: formatDate,
          }}
        />
        <div className="button-container">
          <button
            className="more-options-button"
            onClick={handleMoreOptionsButtonClick}
          >
            More Options...
          </button>
        </div>
        {showMoreOptionsPane && (
          <MoreOptionsPane
            options={[
              { label: "Rename", action: handleRenameConfirm },
              { label: "Delete", action: handleDeleteConfirm },
              { label: "Duplicate", action: handleDuplicateConfirm },
            ]}
            onClose={() => setShowMoreOptionsPane(false)}
          />
        )}
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={setCurrentPage}
        />
        {showCoursePopup && (
          <CoursePopUp
            onClose={() => {
              if (!isLoading) setShowCoursePopup(false);
            }}
            onSubmit={(courseData) => {
              handleCreateCourse(courseData);
            }}
            isLoading={isLoading}
          />
        )}
        {showDuplicatePopup && selectedCourseForDuplicate && (
          <CoursePopUpDuplicate
            onClose={() => {
              if (!isLoading) setShowDuplicatePopup(false);
            }}
            onSubmit={(courseData) => {
              handleDuplicateCourse(courseData);
            }}
            course={selectedCourseForDuplicate}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default CoursesPage;
